import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import { UnsubscribeService } from '@gtd/hotels/data-access/services';
import { markFormTouched } from '@gtd/utilities';
import { debounceTime, filter, takeUntil, tap } from 'rxjs/operators';
import { InsuranceService } from '../../../services/insurance.service';

@Component({
  selector: 'gtd-pvi-pax-form',
  templateUrl: './pvi-pax-form.component.html',
  styleUrls: ['./pvi-pax-form.component.scss'],
  providers: [
    UnsubscribeService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PviPaxFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PviPaxFormComponent),
      multi: true
    }
  ]
})
export class PviPaxFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() index: number;
  @Input() isSubmitted: boolean;
  @Input() numberOfItems: number;
  @Output() onDelete: EventEmitter<number> = new EventEmitter();
  holderIndex$ = this.insuranceService.holderIndex$;
  paxForm: FormGroup;
  constructor(
    private insuranceService: InsuranceService,
    private unsubscribeService: UnsubscribeService
  ) {
    this.paxForm = new FormGroup({
      fullName: new FormControl(),
      birthDay: new FormControl(),
      address: new FormControl(null),
      email: new FormControl(),
      phoneNumber: new FormControl(null, [Validators.pattern('^[0-9]*$')]),
      identityDocument: new FormControl(null, [Validators.required]),
      isHolder: new FormControl(false),
      gender: new FormControl(),
      type: new FormControl()
    });
  }

  ngOnInit() {
    this.holderIndex$
      .pipe(
        filter(
          holderIndex => holderIndex != null && holderIndex !== this.index
        ),
        tap(_ => {
          this.paxForm.get('isHolder').setValue(false, { emitEvent: false });
          this.updateValidators(false);
        }),
        takeUntil(this.unsubscribeService.unsubscribe$)
      )
      .subscribe();

    this.paxForm
      .get('isHolder')
      .valueChanges.pipe(
        filter(isHolder => isHolder),
        tap(_ => {
          this.insuranceService.setHolderIndex(this.index);
          this.updateValidators(true);
        }),
        takeUntil(this.unsubscribeService.unsubscribe$)
      )
      .subscribe();

    this.paxForm.valueChanges
      .pipe(
        debounceTime(300),
        tap(valueChange => {
          this.onChange(valueChange);
        }),
        takeUntil(this.unsubscribeService.unsubscribe$)
      )
      .subscribe();
  }

  private updateValidators(isHolder: boolean): void {
    const emailControl = this.paxForm.get('email');
    const addressControl = this.paxForm.get('address');
    if (isHolder) {
      emailControl.setValidators([Validators.required, Validators.email]);
      addressControl.setValidators([Validators.required]);
    } else {
      emailControl.clearValidators();
      addressControl.clearValidators();
    }
    addressControl.updateValueAndValidity({ emitEvent: true });
    emailControl.updateValueAndValidity({ emitEvent: true });
  }

  deletePax(): void {
    this.onDelete.emit(this.index);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { isSubmitted } = changes;
    if (isSubmitted && isSubmitted.currentValue) {
      markFormTouched(this.paxForm);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeService.destroy();
  }

  private onChange: (value: string) => void;
  private onTouched: () => void;

  validate(_: FormControl) {
    return this.paxForm.valid ? null : { paxForm: { valid: false } };
  }

  public writeValue(value: any): void {
    if (!value) return;
    this.paxForm.patchValue(value);
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {}

  public onBlur(): void {
    this.onTouched();
  }
}
